// libraries
import * as accessoriesSelectors from '@makemydeal/dr-dash-store/selectors/accessoriesSelectors';
import * as compositeSelectors from '@makemydeal/dr-dash-store/selectors/compositeSelectors';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import * as offerSelectors from '@makemydeal/dr-dash-store/selectors/offerSelectors';
import * as vehicleProtectionSelectors from '@makemydeal/dr-dash-store/selectors/vehicleProtectionSelectors';
import * as vehicleProtectionUtils from '@makemydeal/dr-dash-store/utils/vehicleProtectionUtils';
import type { DealState } from '@makemydeal/dr-dash-types';
import * as calculationDetailsSelectors from '@makemydeal/dr-dash-store/selectors/calculationDetailsSelectors';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';

// components
import { Grid } from '@interstate/components/Grid';
import { DealSummaryItemWrapper } from '../common/DealSummaryItemWrapper';
import LineItemWithChildren from '../common/LineItemWithChildren';

// constants
import * as constants from '../../constants';

// utils
import { formatDollarsAndCents } from '@makemydeal/dr-common-utils';

// hooks
import { useMemo } from 'react';
import { createDealRootSelector } from '../../offerCompareColumn/hooks/createDealRootSelector';
import type { LineItem } from '../../types/LineItemTypes';
import { convertAccessoryToLineItem, convertGovernmentFeeToLineItem, convertVppProductToLineItem } from '../utils/transform';
import { useTrackedSelector } from '../../telemetry/useTrackedSelector';
import { useHeldValue } from '../../hooks/useHeldSelector';

export type TotalDueAtSigningProps = DealState & {
    useWrapper?: boolean;
    showLargeValue?: boolean;
    hasDelta?: boolean;
};

const TotalDueAtSigning = ({ deal, useWrapper = true, showLargeValue, hasDelta = false }: TotalDueAtSigningProps) => {
    const useDealRootSelector = createDealRootSelector(deal, 'TotalDueAtSigning');

    const totalAmountDue = useHeldValue(
        useDealRootSelector(offerReduxSelectors.getDueAtSigningFromDueInAdvance, 'dueAtSigningFromDueInAdvance'),
        deal
    );
    const monthlyPayment = useHeldValue(useDealRootSelector(offerSelectors.getMonthlyPayment, 'monthlyPayment'), deal);
    const isOnePayLeaseFirstPaymentEnabled = useTrackedSelector(featureToggleSelectors.isOnePayLeaseFirstPaymentEnabled, {
        component: 'TotalDueAtSigning',
        field: 'isOnePayLeaseFirstPaymentEnabled'
    });
    const isOnePayLease = useDealRootSelector(offerReduxSelectors.isOnePayLease, 'isOnePayLease');
    const firstPaymentAmountRaw = useHeldValue(
        useDealRootSelector(offerReduxSelectors.getFirstPaymentAmount, 'firstPaymentAmount'),
        deal
    );
    const baseMonthlyPaymentTotal = useHeldValue(
        useDealRootSelector(offerReduxSelectors.getBaseMonthlyPaymentTotal, 'baseMonthlyPaymentsTotal'),
        deal
    );
    const firstPaymentAmount =
        isOnePayLeaseFirstPaymentEnabled && isOnePayLease ? firstPaymentAmountRaw ?? baseMonthlyPaymentTotal : monthlyPayment;
    const initialIsFirstPaymentCapitalized = useTrackedSelector(calculationDetailsSelectors.getIsFirstPaymentCapitalized, {
        component: 'TotalDueAtSigning',
        field: 'isFirstPaymentCapitalized'
    });
    const paymentFirstPaymentCapitalized = useTrackedSelector(offerReduxSelectors.getFirstPaymentCapitalized, {
        component: 'TotalDueAtSigning',
        field: 'firstPaymentCapitalized'
    });
    const firstPaymentCapitalized = paymentFirstPaymentCapitalized ?? initialIsFirstPaymentCapitalized;

    const upfrontTaxes = useHeldValue(useDealRootSelector(offerReduxSelectors.getUpfrontTaxTotal, 'upfrontTaxTotal'), deal);
    const additionalTotal = useDealRootSelector(offerSelectors.getAdditionalTaxTotal, 'additionalTaxTotal');

    const secDeposit = useDealRootSelector(
        offerSelectors.getSecurityDepositOverrideWithFallback,
        'securityDepositOverrideWithFallback'
    );

    const acqUpfront = useHeldValue(
        useDealRootSelector(
            (state) => offerSelectors.getAcqFeeUpFrontOverrideWithFallback(state),
            'acqFeeUpFrontOverrideWithFallback'
        ),
        deal
    );
    const acqFeeOverride = useHeldValue(
        useDealRootSelector((state) => offerSelectors.getAcqFeeOverrideWithFallback(state), 'acqFeeOverrideWithFallback'),
        deal
    );
    const acqFee = acqUpfront ? acqFeeOverride : 0;

    const upfrontGovFeesTotal = useHeldValue(
        useDealRootSelector(offerReduxSelectors.getGovernmentUpfrontFeeTotal, 'governmentUpfrontFeeTotal'),
        deal
    );

    const upfrontGovFees = useDealRootSelector(compositeSelectors.getGovernmentFeesUpFront, 'governmentFeesUpFront');
    const govFeeChildren: LineItem[] = upfrontGovFees.map(convertGovernmentFeeToLineItem);

    const upfrontDealerFees = useDealRootSelector(
        (state) => compositeSelectors.getDealerFeesTotal(state, false),
        'dealerFeesTotal'
    );

    const capCostReductionTax = useHeldValue(
        useDealRootSelector(offerReduxSelectors.getCapCostReductionTax, 'capCostReductionTax'),
        deal
    );
    const salesAndUseTax = useDealRootSelector(offerSelectors.getPrimaryTaxTotal, 'primaryTaxTotal');
    const isCrtCapped = useDealRootSelector(
        offerReduxSelectors.getCapReductionTaxCapitalizedIndicator,
        'capReductionTaxCapitalizedIndicator'
    );
    const isTaxCapped = useDealRootSelector(offerReduxSelectors.isTaxCapped, 'taxCapped');

    const rebateTotal = useDealRootSelector(
        offerReduxSelectors.getAppliedIncentivesTotalForNonDealerCash,
        'appliedIncentivesTotalForNonDealerCash'
    );

    const upfrontProtectionProductsTotal = useDealRootSelector(vehicleProtectionSelectors.getUpfrontVppTotal, 'upfrontVppTotal');
    const vppProducts = useDealRootSelector(vehicleProtectionSelectors.getVppProducts, 'vppProducts');
    const upfrontProtectionProductsItems: LineItem[] = useMemo(
        () =>
            vppProducts
                .filter(vehicleProtectionUtils.isVppProductSelected)
                .filter(vehicleProtectionUtils.isVppUpfrontProduct)
                .map(convertVppProductToLineItem),
        [vppProducts]
    );
    const accessoriesTotal = useDealRootSelector(accessoriesSelectors.getUpfrontAccessoriesTotal, 'upfrontAccessoriesTotal');
    const itemizedAccessories = useDealRootSelector(accessoriesSelectors.getUpfrontAccessoriesList, 'upfrontAccessoriesList').map(
        convertAccessoryToLineItem
    );

    return (
        <DealSummaryItemWrapper
            label={constants.TOTAL_DUE_AT_SIGNING}
            value={formatDollarsAndCents(totalAmountDue)}
            useWrapper={useWrapper}
            showLargeValue={showLargeValue}
            hasDelta={hasDelta}
        >
            <Grid>
                {!firstPaymentCapitalized && (
                    <LineItemWithChildren label={constants.MO_PAYMENT} value={formatDollarsAndCents(firstPaymentAmount)} />
                )}
                <LineItemWithChildren
                    label={constants.GOV_FEES}
                    value={formatDollarsAndCents(upfrontGovFeesTotal)}
                    childItems={govFeeChildren}
                />
                <LineItemWithChildren label={constants.DOC_FEES} value={formatDollarsAndCents(upfrontDealerFees)} />
                <LineItemWithChildren label={constants.ACQUISITION_FEE} value={formatDollarsAndCents(acqFee)} />
                <LineItemWithChildren
                    label={constants.ACCESSORIES}
                    value={formatDollarsAndCents(accessoriesTotal)}
                    childItems={itemizedAccessories}
                />
                <LineItemWithChildren
                    label={constants.PROTECTION}
                    value={formatDollarsAndCents(upfrontProtectionProductsTotal)}
                    childItems={upfrontProtectionProductsItems}
                />
                <LineItemWithChildren label={constants.SECURITY_DEPOSIT} value={formatDollarsAndCents(secDeposit)} />
                {/* From product: Only show CCRT if it has a value; most dealers don't pay CCRT */}
                <LineItemWithChildren
                    label={constants.UPFRONT_TAXES}
                    value={formatDollarsAndCents(upfrontTaxes)}
                    childItems={[
                        {
                            label: constants.CAP_COST_REDUCTION_TAX,
                            value: formatDollarsAndCents(isCrtCapped ? 0 : capCostReductionTax)
                        },
                        {
                            label: constants.ADVANCE_SALES_AND_USE_TAX,
                            value: formatDollarsAndCents(isTaxCapped ? 0 : salesAndUseTax)
                        },
                        {
                            label: constants.ADDL_TAXES,
                            value: formatDollarsAndCents(isTaxCapped ? 0 : additionalTotal)
                        }
                    ]}
                />
                <LineItemWithChildren label={constants.REBATES} value={formatDollarsAndCents(rebateTotal)} />
            </Grid>
        </DealSummaryItemWrapper>
    );
};

export default TotalDueAtSigning;
